"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { cn, toSafeErr } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import LabeledField from "./labeled-field";
// import GoogleLoginButton from "../button/google-login-button";
import { PasswordInput } from "./password-input";
import { useAuthStore } from "@/stores/auth-store";

type FieldErrorMap = Partial<
  Record<"email" | "password", string[] | undefined>
>;

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const router = useRouter();

  const [errors, setErrors] = useState<FieldErrorMap>({});
  const [message, setMessage] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    setMessage(null);

    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") || "")
      .trim()
      .toLowerCase();
    const password = String(fd.get("password") || "");

    // Validate form fields
    const local: Partial<FieldErrorMap> = {};
    if (!email) local.email = ["Email is required"];
    if (!password) local.password = ["Password is required"];
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // include cookies
        body: JSON.stringify({ email, password }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {}

      if (res.ok) {
        useAuthStore.getState().setUser(data.user);
        if (data.user.role === "ADMIN") {
          router.replace("/admin");
        } else {
          router.replace("/profile");
        }
        return;
      }

      if (data?.errors) setErrors(data.errors as FieldErrorMap);
      setMessage(data?.error || "Login failed. Please check your inputs.");
    } catch (err) {
      const safeError = toSafeErr(err);
      console.error("Login error:", safeError);
      setMessage("Network error. Please try again.");
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Login to your account</h2>
          </CardTitle>
          <CardDescription>
            Enter your email below to login to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} noValidate>
            <div className="flex flex-col gap-5">
              <LabeledField
                label="Email"
                id="email"
                type="email"
                placeholder="m@example.com"
                autoComplete="email"
                name="email"
                error={errors.email}
              />
              <LabeledField
                label="Password"
                id="password"
                error={errors.password}
                labelSide={
                  <Link
                    href="#"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  >
                    Forgot your password?
                  </Link>
                }
              >
                <PasswordInput
                  id="password"
                  placeholder="Enter your password"
                  autoComplete="password"
                  name="password"
                  error={errors.password}
                  required
                />
              </LabeledField>
              <Button type="submit" className="w-full mt-2">
                Login
              </Button>
            </div>
          </form>
          {message && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {message}
            </p>
          )}
          {/* <div className="relative mt-10 mb-6">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <span className="block w-full border-t border-gray-200"></span>
            </div>
            <div className="relative flex justify-center text-sm font-medium">
              <span className="bg-white px-6 text-gray-900">
                Or continue with
              </span>
            </div>
          </div> */}
          {/* <GoogleLoginButton /> */}
        </CardContent>
        <div className="mt-4 text-center text-sm">
          Don&apos;t have an account?{" "}
          <Link href="/signup" className="underline underline-offset-4">
            Sign up
          </Link>
        </div>
      </Card>
    </div>
  );
}
